import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Slider from "react-slick";
import { CategoriesServices } from "../services/CategoriesServices";

const FeatureOne = () => {
  const sliderRef = useRef(null);

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const count = categories.length;
  const isSingle = count === 1;

  const settings = useMemo(
    () => ({
      dots: false,
      arrows: false,
      infinite: count > 10,
      speed: 1000,
      slidesToShow: Math.min(10, Math.max(1, count)),
      slidesToScroll: 1,
      initialSlide: 0,
      responsive: [
        { breakpoint: 1699, settings: { slidesToShow: Math.min(9, Math.max(1, count)) } },
        { breakpoint: 1599, settings: { slidesToShow: Math.min(8, Math.max(1, count)) } },
        { breakpoint: 1399, settings: { slidesToShow: Math.min(6, Math.max(1, count)) } },
        { breakpoint: 992, settings: { slidesToShow: Math.min(5, Math.max(1, count)) } },
        { breakpoint: 768, settings: { slidesToShow: Math.min(4, Math.max(1, count)) } },
        { breakpoint: 575, settings: { slidesToShow: Math.min(3, Math.max(1, count)) } },
        { breakpoint: 424, settings: { slidesToShow: Math.min(2, Math.max(1, count)) } },
        { breakpoint: 359, settings: { slidesToShow: 1 } },
      ],
    }),
    [count]
  );

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);
        const data = await CategoriesServices.listAll();

        const normalized = (data || [])
          .map((c) => ({
            id: c.id,
            name: c.name || c.title || "Category",
            image: c.image,
            slug: c.slug,
            parent_id: Number(c.parent_id ?? 0),
          }))
          .filter((c) => c.id)
          .filter((c) => c.parent_id === 0);

        if (alive) setCategories(normalized);
      } catch (err) {
        console.error("Failed to load categories:", err);
        if (alive) setCategories([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const renderCategoryCard = (item) => (
    <div className="feature-item text-center">
      <div className="feature-item__thumb rounded-circle">
        <Link
          to={`/shop?cat=${item.id}`}
          className="w-100 h-100 flex-center"
        >
          {item.image ? (
            <img src={item.image} alt={item.name} />
          ) : (
            <i className="ph ph-image text-2xl" />
          )}
        </Link>
      </div>

      <div className="feature-item__content mt-16">
        <h6 className="text-lg mb-8">
          <Link to={`/shop?cat=${item.id}`} className="text-inherit">
            {item.name}
          </Link>
        </h6>
      </div>
    </div>
  );

  if (!loading && categories.length === 0) {
    return null;
  }

  return (
    <div className="feature" id="featureSection">
      <style>{`
        .feature-item__thumb {
          overflow: hidden;
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .feature-item__thumb > a {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
          overflow: hidden;
          border-radius: 50%;
        }

        .feature-item__thumb img {
          width: 70%;
          height: 70%;
          object-fit: contain;
          display: block;
          transition: transform 0.3s ease;
          pointer-events: none;
        }

        .feature-item__thumb:hover img {
          transform: scale(1.12);
        }

        .feature-single-wrap {
          display: flex;
          justify-content: center;
          align-items: center;
          width: 100%;
        }

        .feature-single-item {
          width: 100%;
          max-width: 220px;
        }
      `}</style>

      <div className="container container-lg">
        <div className="position-relative arrow-center">
          {!isSingle && count > 1 && (
            <div className="flex-align">
              <button
                type="button"
                onClick={() => sliderRef.current?.slickPrev()}
                className="slick-prev slick-arrow flex-center rounded-circle bg-white text-xl hover-bg-main-600 hover-text-white transition-1"
                aria-label="Previous"
              >
                <i className="ph ph-caret-left" />
              </button>

              <button
                type="button"
                onClick={() => sliderRef.current?.slickNext()}
                className="slick-next slick-arrow flex-center rounded-circle bg-white text-xl hover-bg-main-600 hover-text-white transition-1"
                aria-label="Next"
              >
                <i className="ph ph-caret-right" />
              </button>
            </div>
          )}

          <div className="feature-item-wrapper">
            {loading ? (
              <div className="p-3">Loading categories...</div>
            ) : isSingle ? (
              <div className="feature-single-wrap">
                <div className="feature-single-item">
                  {renderCategoryCard(categories[0])}
                </div>
              </div>
            ) : (
              <Slider ref={sliderRef} {...settings}>
                {categories.map((item) => (
                  <div key={item.id}>
                    {renderCategoryCard(item)}
                  </div>
                ))}
              </Slider>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureOne;